"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { useLocale } from "next-intl";
import { localeItems } from "@/i18n/routing";
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ClickAwayListener } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import Cookies from "js-cookie";
import LocaleDropdownLinkList from "./locale-selector-list-links";

function getQueryParams(): Record<string, string> {
    if (typeof window !== "undefined") {
        const params = new URLSearchParams(window.location.search);
        const queryParams: Record<string, string> = {};
        params.forEach((value, key) => {
            queryParams[key] = value;
        });
        return queryParams;
    }
    return {};
}

function IsNotCurrentLocale(loc: any) {
    return (loc.id !== useLocale());
}

function IsCurrentLocale(loc: any) {
    return (loc.id === useLocale());
}


const LocaleDropdown = () => {
    const filteredLocaleItems = localeItems.filter(IsNotCurrentLocale);
    const currentLocaleItem = localeItems.filter(IsCurrentLocale)[0];
    const [isOpen, setIsOpen] = useState(false);
    const currentLocale = useLocale();

    const queryParams = getQueryParams();

    // Set initial site-language cookie on component mount if it doesn't exist
    useEffect(() => {
        const currentSiteLangCookie = Cookies.get("site-language");

        if (!currentSiteLangCookie || currentSiteLangCookie === "") {
            // Extract country code from locale (e.g., 'en-us' -> 'us')
            const countrySuffix = currentLocale.split("-")[1]?.toLowerCase();
            const siteId = `collette-${countrySuffix}`;

            // Special case for en-us locale: language should be just 'en'
            const langValue = currentLocale === "en-us" ? "en" : currentLocale.toLowerCase();

            // Set the legacy cookie with site and language values

            // Base cookie options for all environments
            const cookieOptions: {
                expires: number;
                path: string;
                sameSite: "strict";
                secure: boolean;
                domain?: string;
            } = {
                expires: 365,
                path: "/",
                sameSite: "strict",
                secure: true
            };

            // Add domain specific settings based on hostname
            if (typeof window !== "undefined") {
                const { hostname } = window.location;

                if (hostname.includes("gocollette.com")) {
                    // Production environment
                    cookieOptions.domain = ".gocollette.com";
                } else if (hostname.includes("internal.collette.cloud")) {
                    // Development or QA environments
                    cookieOptions.domain = ".internal.collette.cloud";
                }
                // For localhost or other environments, don't set domain
            }

            Cookies.set("site-language", `site=${siteId}&language=${langValue}`, cookieOptions);
        }
    }, [currentLocale]);

    const handleClickAway = () => {
        if (isOpen) {
            setIsOpen(false);
        }
    };

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ paddingTop: 1.8 }} data-testid="locale-dropdown">

                <List sx={{
                    paddingTop: 0,
                    paddingBottom: 0,
                    margin: 0
                }}>


                    <ListItem sx={{
                        padding: 0,
                        margin: 0
                    }} >

                        <ListItemButton
                            data-testid="current-locale-button"
                            onClick={toggleDropdown}
                            sx={[
                                {
                                    color: "var(--isagent-text-color)",
                                    height: 30,
                                    "&:hover": {
                                        backgroundColor: "background.default",
                                        color: "primary.main",
                                        borderColor: "primary.main",
                                        borderBottom: 1
                                    }
                                },
                                isOpen && {
                                    backgroundColor: "background.default",
                                    color: "var(--isagent-text-color)",
                                    borderColor: "primary.main",
                                    borderBottom: 1
                                }

                            ]} >

                            <ListItemIcon data-testid="current-locale-icon" sx={{ paddingRight: 0, minWidth: 20 }}>
                                <Image src={currentLocaleItem.icon}
                                    alt={`${currentLocaleItem.alt}-icon`}
                                    width={16}
                                    height={16}
                                />
                            </ListItemIcon>
                            <ListItemText data-testid="current-locale-text"
                                primary={`${currentLocaleItem.label} `} />
                            <ChevronRight sx={{ marginLeft: 3 }} />

                        </ListItemButton>

                    </ListItem>


                    {isOpen && <LocaleDropdownLinkList localeDropdownItems={filteredLocaleItems} queryParams={queryParams}/>}

                </List>
            </Box>
        </ClickAwayListener>


    );

};

export default LocaleDropdown;